import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import {
    activeData3dAtom,
    comparisonData3dAtom,
    currentFrameAtom,
    selectedAxisFocusIdAtom,
    syncedFrameAtom,
} from '@store';

import {
    ANGLE_AXES,
    DISPLACEMENT_AXES,
    DISTANCE_AXES,
    JOINT_ANGLE_AXES,
    VISUALIZATION_AXES_PARENT_MAP,
    VISUALIZATION_MEASUREMENT_TYPES,
    VISUALIZATION_MEASUREMENTS,
} from '../mappings';
import { computeAngleDirections } from '../useAngles';
import { MemoAngleVisualization } from '../visualizations/AngleVisualization';
import { MemoDisplacementVisualization } from '../visualizations/DisplacementVisualization';

type FocusAxis =
    | { type: 'displacement'; axis: (typeof DISPLACEMENT_AXES)[keyof typeof DISPLACEMENT_AXES] }
    | { type: 'angle'; axis: (typeof ANGLE_AXES)[keyof typeof ANGLE_AXES] }
    | { type: 'jointAngle'; axis: (typeof JOINT_ANGLE_AXES)[keyof typeof JOINT_ANGLE_AXES] }
    | { type: 'distance'; axis: (typeof DISTANCE_AXES)[keyof typeof DISTANCE_AXES] };

function getFocusAxis(focusId: string): FocusAxis | undefined {
    const displacement = DISPLACEMENT_AXES[focusId];

    if (displacement) {
        return { type: 'displacement', axis: displacement };
    }

    const angle = ANGLE_AXES[focusId];

    if (angle) {
        return { type: 'angle', axis: angle };
    }

    const jointAngle = JOINT_ANGLE_AXES[focusId];

    if (jointAngle) {
        return { type: 'jointAngle', axis: jointAngle };
    }

    const distance = DISTANCE_AXES[focusId];

    if (distance) {
        return { type: 'distance', axis: distance };
    }
    return undefined;
}

const useFocus = () => {
    const axisFocusId = useAtomValue(selectedAxisFocusIdAtom);

    return useMemo(() => {
        if (!axisFocusId) return;
        const focus = getFocusAxis(axisFocusId);
        if (!focus) return;
        const measurementId = VISUALIZATION_AXES_PARENT_MAP[axisFocusId];
        const measurement = VISUALIZATION_MEASUREMENTS[measurementId];
        const measurementType = VISUALIZATION_MEASUREMENT_TYPES[measurementId];
        return { ...focus, measurement, measurementId, measurementType };
    }, [axisFocusId]);
};

const CURRENT = 'current';

export const Visualizations = ({ origin = CURRENT }) => {
    const focus = useFocus();
    const currentFrame = useAtomValue(origin === CURRENT ? currentFrameAtom : syncedFrameAtom);
    const data = useAtomValue(origin === CURRENT ? activeData3dAtom : comparisonData3dAtom);

    const directions = useMemo(() => {
        return computeAngleDirections(focus?.axis.camera, data?.measurements, currentFrame);
    }, [focus?.axis.camera, currentFrame, data?.measurements]);

    if (!data || !focus) return null;

    const currentFocus = {
        axis: focus.axis.axisIndex,
        measurement: focus.measurementId,
    };

    return (
        <>
            {focus?.type === 'displacement' && (
                <MemoDisplacementVisualization
                    frame={currentFrame}
                    referenceFrame={data.segmentation['P1']}
                    targetPoints={focus?.axis.targetPoints ?? []}
                    skeleton={data.skeleton}
                    keypointIdToName={data.keypointIdToName}
                    currentFocus={currentFocus}
                    opacity={1}
                />
            )}
            {focus?.type === 'angle' && (
                <MemoAngleVisualization
                    type={VISUALIZATION_MEASUREMENT_TYPES[focus.measurementId]}
                    frame={currentFrame}
                    skeleton={data.skeleton}
                    targetPoints={focus?.axis.targetPoints ?? []}
                    keypointIdToName={data.keypointIdToName}
                    currentFocus={currentFocus}
                    referenceDirection={directions?.referenceDirection}
                    actualDirection={directions?.actualDirection}
                    opacity={1}
                />
            )}
            {focus?.type === 'distance' && (
                <MemoDisplacementVisualization
                    frame={currentFrame}
                    skeleton={data.skeleton}
                    targetPoints={focus?.axis.targetPoints ?? []}
                    keypointIdToName={data.keypointIdToName}
                    currentFocus={currentFocus}
                    opacity={1}
                />
            )}
            {focus?.type === 'jointAngle' && (
                <MemoAngleVisualization
                    type={focus.measurementType}
                    frame={currentFrame}
                    skeleton={data.skeleton}
                    targetPoints={focus?.axis.targetPoints ?? []}
                    keypointIdToName={data.keypointIdToName}
                    currentFocus={currentFocus}
                    opacity={1}
                />
            )}
        </>
    );
};
